// Mantis #1453 — M001-D029 §5: the Access Authority /groups editor composite.
// Mounted by the #1371 shell page (RealmAuthorityGroupsPage) at the real
// route /app/admin/realm-authority/groups.
//
// Cardinality is 1:1 (lead ruling): one RealmAuthority realm per
// organisation. There is no installation id anywhere here — the #1378
// connectInstallationId scoping (multiple RA installations per org) is
// retired. `linked:false` is an explicit, renderable state read from the
// contract's own `linked` field, never inferred from absence.
//
// Owns, exactly as the access-schedule panel does:
// - request-generation fencing across org/user switches (mirror data must
//   never render across a switch);
// - the StepUpDialog machinery (F0308): a 401 carrying a real challenge parks
//   the failing write and replays it after satisfaction — access_control_manage
//   and SAL-4 freshness are enforced Core-side; this page follows the server;
// - M004-F0629: the link status is POLLED, not read once at mount. Polling
//   /link (the lightest of the three reads — no rows) was chosen over an
//   explicit Refresh control because the state that matters — "Save is
//   disabled because RealmAuthority is offline" — is exactly the state in
//   which the operator is waiting, and a control they must know to press
//   is a reload by another name. The interval is short while the tunnel is
//   down (that is when re-enabling Save matters) and long while it is up
//   (only to notice an outage before a save hits Core's 409). Groups and
//   directory are NOT polled — they change only by RealmAuthority publish
//   or by this page's own saves, which already re-fetch.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import StepUpDialog from '../../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../../utils/stepUp';
import { realmAuthorityGroupsApi } from '../../../services/realmAuthorityGroupsApi';
import { projectDirectoryView, projectGroupsView, projectLinkStatus } from './normalisers';
import RealmAuthorityLinkStatus from './RealmAuthorityLinkStatus';
import RealmAuthorityGroupList from './RealmAuthorityGroupList';
import RealmAuthorityGroupEditor from './RealmAuthorityGroupEditor';
import './realmAuthority.css';

const errorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.response?.data?.error?.message
  || error?.message
  || fallback
);

// Read failure classes (WEB-RA-GROUPS-002 discipline: the page must never
// fabricate rows and must name why no listing is shown):
// - 'absent'   — Core has no realm-authority route for this session (404):
//                there is genuinely no group listing available yet;
// - 'elevation'— 401/403: Core can answer, but this session is not an
//                elevated administrator, so it is not shown the listing;
// - 'error'    — anything else (network, 5xx, unverifiable payload).
const failureClassOf = (error) => {
  const status = error?.response?.status;
  if (status === 404) return 'absent';
  if (status === 401 || status === 403) return 'elevation';
  return 'error';
};

const isCancelled = (error, signal) => (
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || signal?.aborted
);

const stringId = (value) => (value == null ? '' : String(value));

export const LINK_POLL_MS_DOWN = 5000;
export const LINK_POLL_MS_UP = 30000;

const sameLink = (a, b) => (
  a?.linked === b?.linked && a?.tunnelConnected === b?.tunnelConnected
  && a?.lastSnapshotAt === b?.lastSnapshotAt && a?.installationUuid === b?.installationUuid
);

export default function RealmAuthorityGroupsEditor({ organisationId }) {
  const [link, setLink] = useState(null);
  const [groupsView, setGroupsView] = useState(null);
  const [directoryView, setDirectoryView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [failureClass, setFailureClass] = useState('');
  const [selectedGroupSid, setSelectedGroupSid] = useState('');
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const requestSequence = useRef(0);
  const writeLock = useRef(false);
  const activeOrganisationId = useRef(stringId(organisationId));
  const retryWrite = useRef(null);

  activeOrganisationId.current = stringId(organisationId);

  const load = useCallback(async ({ signal } = {}) => {
    const requestedOrganisationId = stringId(organisationId);
    if (!requestedOrganisationId) return;
    const sequence = ++requestSequence.current;
    const stillCurrent = () => sequence === requestSequence.current
      && activeOrganisationId.current === requestedOrganisationId
      && !signal?.aborted;

    setLoading(true);
    setError('');
    setFailureClass('');
    const [linkResult, groupsResult, directoryResult] = await Promise.allSettled([
      realmAuthorityGroupsApi.getLinkStatus(requestedOrganisationId, { signal }),
      realmAuthorityGroupsApi.getGroups(requestedOrganisationId, { signal }),
      realmAuthorityGroupsApi.getDirectory(requestedOrganisationId, { signal }),
    ]);
    if (!stillCurrent()) return;

    const rejected = [linkResult, groupsResult, directoryResult].filter((r) => r.status === 'rejected');
    if (rejected.some((r) => isCancelled(r.reason, signal))) return;

    if (rejected.length) {
      // Worst-first: an elevation refusal is the most specific honest signal
      // even if another call happened to answer; never let a lucky 200 on
      // one endpoint hide that another refused this session outright.
      const byClass = rejected.map((r) => ({ class: failureClassOf(r.reason), reason: r.reason }));
      const pick = byClass.find((r) => r.class === 'elevation')
        || byClass.find((r) => r.class === 'absent')
        || byClass[0];
      setLink(null);
      setGroupsView(null);
      setDirectoryView(null);
      setFailureClass(pick.class);
      setError(errorMessage(pick.reason, 'RealmAuthority groups are not available right now.'));
      setLoading(false);
      return;
    }

    const nextLink = projectLinkStatus(linkResult.value);
    const nextGroups = projectGroupsView(groupsResult.value);
    const nextDirectory = projectDirectoryView(directoryResult.value);
    if (!nextLink || !nextGroups || !nextDirectory) {
      setLink(null);
      setGroupsView(null);
      setDirectoryView(null);
      setFailureClass('error');
      setError('The RealmAuthority response could not be verified. Nothing is displayed.');
      setLoading(false);
      return;
    }

    setLink(nextLink);
    setGroupsView(nextGroups);
    setDirectoryView(nextDirectory);
    setSelectedGroupSid((current) => (nextGroups.groups.some((g) => g.groupSid === current) ? current : ''));
    setLoading(false);
  }, [organisationId]);

  useEffect(() => {
    requestSequence.current += 1;
    retryWrite.current = null;
    setLink(null);
    setGroupsView(null);
    setDirectoryView(null);
    setError('');
    setFailureClass('');
    setStepUpChallenge(null);
    setSelectedGroupSid('');
    if (!organisationId) return undefined;
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [organisationId, load]);

  // M004-F0629: keep `link` live. Fenced by organisation (a poll answer for
  // a previous org never lands) and by its own cancel flag; it never bumps
  // requestSequence, so it cannot cancel a full load in flight. A poll that
  // fails or returns an unverifiable payload changes nothing — the last
  // verified state stands rather than flapping to a guess.
  const linkLinked = link?.linked === true;
  const linkTunnelConnected = link?.tunnelConnected === true;
  useEffect(() => {
    if (!linkLinked) return undefined;
    const pollOrganisationId = stringId(organisationId);
    const controller = new AbortController();
    let cancelled = false;
    let timer = null;
    const delay = linkTunnelConnected ? LINK_POLL_MS_UP : LINK_POLL_MS_DOWN;
    const tick = async () => {
      try {
        const response = await realmAuthorityGroupsApi.getLinkStatus(pollOrganisationId, { signal: controller.signal });
        if (cancelled || activeOrganisationId.current !== pollOrganisationId) return;
        const next = projectLinkStatus(response);
        if (next) setLink((current) => (sameLink(current, next) ? current : next));
      } catch (pollError) {
        // Leave the last verified state in place; the next tick retries.
      }
      // Re-arm unless unmounted/re-keyed. A state change (up<->down)
      // re-runs this effect with the other interval and cancels this chain.
      if (!cancelled) timer = setTimeout(tick, delay);
    };
    timer = setTimeout(tick, delay);
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [organisationId, linkLinked, linkTunnelConnected]);

  const performWrite = useCallback(async (operation) => {
    if (writeLock.current) return;
    writeLock.current = true;
    const writeOrganisationId = stringId(organisationId);
    try {
      await operation();
      retryWrite.current = null;
      setStepUpChallenge(null);
    } catch (writeError) {
      const challenge = getStepUpChallengeFromError(writeError);
      if (challenge?.id) {
        retryWrite.current = { organisationId: writeOrganisationId, operation };
        setStepUpChallenge(challenge);
      } else {
        throw writeError;
      }
    } finally {
      writeLock.current = false;
    }
  }, [organisationId]);

  const finishStepUp = async () => {
    const retry = retryWrite.current;
    setStepUpChallenge(null);
    retryWrite.current = null;
    if (!retry || retry.organisationId !== activeOrganisationId.current) return;
    await performWrite(retry.operation);
  };

  const cancelStepUp = () => {
    retryWrite.current = null;
    setStepUpChallenge(null);
  };

  if (!organisationId) {
    return (
      <section className="ra-editor-empty" data-testid="ra-editor-no-org">
        <h2>Choose one organisation</h2>
        <p>RealmAuthority groups are tenant-specific. Select an organisation first.</p>
      </section>
    );
  }

  const selectedGroup = (groupsView?.groups || []).find((g) => g.groupSid === selectedGroupSid) || null;

  return (
    <div className="ra-editor-root">
      {loading && !groupsView ? <p role="status" data-testid="ra-editor-loading">Loading RealmAuthority groups…</p> : null}
      {/* WEB-RA-GROUPS-002 defect class — the listing area may never render a
          half-state. While no verified listing is shown — including while the
          read is still loading — exactly one honest "no group listing" line
          is present, plus the specific reason Core gave once known. No
          fabricated rows in any of these states. */}
      {groupsView === null ? (
        <p className="ra-groups-empty" data-testid="ra-groups-empty">No group listing is available yet.</p>
      ) : null}
      {groupsView === null && failureClass === 'elevation' ? (
        <p className="ra-editor-elevation" role="status" data-testid="ra-editor-elevation">
          Elevated administrator session required to view RealmAuthority groups.
        </p>
      ) : null}
      {groupsView === null && failureClass === 'error' ? (
        <div className="ra-editor-error" role="alert">{error}</div>
      ) : null}

      {groupsView !== null ? (
        <section className="ra-scope" data-testid="ra-groups-editor">
          <RealmAuthorityLinkStatus link={link} />
          {!groupsView.linked ? (
            <p data-testid="ra-editor-not-linked">
              RealmAuthority is not linked to this organisation. The link is made
              from the Service link page; RealmAuthority itself is configured only
              from its own console.
            </p>
          ) : (
            <>
              <RealmAuthorityGroupList
                groups={groupsView.groups}
                selectedGroupSid={selectedGroupSid}
                onSelect={(groupSid) => setSelectedGroupSid((current) => (current === groupSid ? '' : groupSid))}
              />
              {selectedGroup ? (
                <RealmAuthorityGroupEditor
                  key={selectedGroup.groupSid}
                  organisationId={stringId(organisationId)}
                  link={link}
                  group={selectedGroup}
                  directory={directoryView?.snapshot?.members || []}
                  groups={groupsView.groups}
                  performWrite={performWrite}
                  onReload={() => load()}
                />
              ) : null}
            </>
          )}
        </section>
      ) : null}

      <StepUpDialog
        challenge={stepUpChallenge}
        title="Verify this Access Authority change"
        description="RealmAuthority membership changes require a fresh high-security verification."
        onComplete={finishStepUp}
        onCancel={cancelStepUp}
      />
    </div>
  );
}
